import { useState } from 'react';
import { ArrowLeft } from 'lucide-react';
import { useRequestPasswordReset } from '@/hooks/use-password-reset';

export function ForgotPasswordPage() {
  const requestReset = useRequestPasswordReset();
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [submitted, setSubmitted] = useState(false);
  const [resetToken, setResetToken] = useState<string | null>(null);

  const validate = () => {
    const errors: Record<string, string> = {};
    if (!email.trim()) errors.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) errors.email = 'Please enter a valid email';
    return errors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const errors = validate();
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;

    try {
      const res = await requestReset.mutateAsync({ email });
      setSubmitted(true);
      if (res.data.resetToken) {
        setResetToken(res.data.resetToken);
      }
    } catch (err: any) {
      setError(err?.error?.message || 'Failed to send reset request');
    }
  };

  if (submitted) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-muted/30">
        <div className="w-full max-w-sm space-y-6 rounded-xl border border-border bg-white p-8 shadow-sm">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-foreground">Check Your Email</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              If an account with that email exists, we've sent password reset instructions.
            </p>
          </div>

          {resetToken && (
            <div className="rounded-lg bg-blue-50 px-4 py-3 text-sm text-blue-700">
              <p className="font-medium">Development mode</p>
              <p className="mt-1 break-all font-mono text-xs">{resetToken}</p>
              <a
                href={`#/reset-password?token=${resetToken}`}
                className="mt-2 inline-block text-xs font-medium text-blue-600 hover:underline"
              >
                Reset Password Now
              </a>
            </div>
          )}

          <a
            href="#/login"
            className="flex items-center justify-center gap-2 text-sm font-medium text-primary hover:underline"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Login
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/30">
      <div className="w-full max-w-sm space-y-6 rounded-xl border border-border bg-white p-8 shadow-sm">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-foreground">Forgot Password?</h1>
          <p className="mt-1 text-sm text-muted-foreground">Enter your email to receive reset instructions</p>
        </div>

        <form onSubmit={handleSubmit} noValidate className="space-y-4">
          {error && <div className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-600">{error}</div>}

          <div>
            <label className="mb-1 block text-sm font-medium text-foreground">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setFieldErrors((prev) => ({ ...prev, email: '' }));
              }}
              autoFocus
              className={`h-10 w-full rounded-lg border px-3 text-sm outline-none focus:ring-1 ${fieldErrors.email ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-input focus:border-primary focus:ring-primary'}`}
              placeholder="you@example.com"
            />
            {fieldErrors.email && <p className="mt-1 text-xs text-red-500">{fieldErrors.email}</p>}
          </div>

          <button
            type="submit"
            disabled={requestReset.isPending}
            className="h-10 w-full rounded-lg bg-primary text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {requestReset.isPending ? 'Sending...' : 'Send Reset Link'}
          </button>
        </form>

        <a
          href="#/login"
          className="flex items-center justify-center gap-2 text-sm font-medium text-primary hover:underline"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Login
        </a>
      </div>
    </div>
  );
}
